/**
 * Report Generator Service
 * 
 * Generates comprehensive PDF reports for Elite users
 */

import { ScanRecord } from './scanService';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

export interface MonthlyReport {
  month: string;
  year: number;
  totalScans: number;
  averageGlowScore: number;
  glowScoreTrend: 'improving' | 'stable' | 'declining';
  scans: ScanRecord[];
  topRecommendations: string[];
  progressHighlights: string[];
  dietPlansCount: number;
  masksTried: number;
}

/**
 * Generate monthly report data
 */
export const generateMonthlyReport = (
  scans: ScanRecord[],
  month: Date = new Date()
): MonthlyReport => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  
  // Filter scans for the month
  const monthScans = scans.filter(scan => {
    const scanDate = new Date(scan.createdAt || scan.timestamp);
    return scanDate >= monthStart && scanDate <= monthEnd;
  });

  // Calculate average glow score
  const scores = monthScans
    .map(scan => scan.analysis?.skinHealthIndex || scan.glowScore || 0)
    .filter(score => score > 0);
  const averageGlowScore = scores.length > 0
    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
    : 0;

  // Determine trend
  const sortedScans = [...monthScans].sort((a, b) => 
    new Date(a.createdAt || a.timestamp).getTime() - new Date(b.createdAt || b.timestamp).getTime()
  );
  
  let glowScoreTrend: 'improving' | 'stable' | 'declining' = 'stable';
  if (sortedScans.length >= 2) {
    const firstScore = sortedScans[0].analysis?.skinHealthIndex || sortedScans[0].glowScore || 0;
    const lastScore = sortedScans[sortedScans.length - 1].analysis?.skinHealthIndex || 
                     sortedScans[sortedScans.length - 1].glowScore || 0;
    const difference = lastScore - firstScore;
    
    if (difference > 5) glowScoreTrend = 'improving';
    else if (difference < -5) glowScoreTrend = 'declining';
  }

  // Collect all recommendations
  const allRecommendations = monthScans
    .flatMap(scan => scan.analysis?.recommendations || [])
    .filter((rec, index, self) => self.indexOf(rec) === index) // Unique
    .slice(0, 10);

  // Collect progress highlights
  const allHighlights = monthScans
    .flatMap(scan => scan.analysis?.progressHighlights || [])
    .filter((highlight, index, self) => self.indexOf(highlight) === index) // Unique
    .slice(0, 5);

  return {
    month: format(month, 'MMMM'),
    year: month.getFullYear(),
    totalScans: monthScans.length,
    averageGlowScore,
    glowScoreTrend,
    scans: monthScans,
    topRecommendations: allRecommendations,
    progressHighlights: allHighlights,
    dietPlansCount: 0, // TODO: Count from saved diet plans
    masksTried: 0, // TODO: Count from saved masks
  };
};

/**
 * Generate PDF report content (HTML format for now, can be converted to PDF)
 */
export const generatePDFContent = (report: MonthlyReport, userName: string): string => {
  const reportDate = format(new Date(), 'MMMM dd, yyyy');
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>GlowLens Monthly Report - ${report.month} ${report.year}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
    }
    .header {
      text-align: center;
      border-bottom: 3px solid #3b82f6;
      padding-bottom: 1rem;
      margin-bottom: 2rem;
    }
    .header h1 {
      color: #3b82f6;
      margin: 0;
      font-size: 2rem;
    }
    .header p {
      color: #666;
      margin: 0.5rem 0 0 0;
    }
    .section {
      margin-bottom: 2rem;
    }
    .section h2 {
      color: #1f2937;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 0.5rem;
      margin-bottom: 1rem;
    }
    .stat-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    .stat-card {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 8px;
      border-left: 4px solid #3b82f6;
    }
    .stat-label {
      font-size: 0.875rem;
      color: #6b7280;
      margin-bottom: 0.25rem;
    }
    .stat-value {
      font-size: 1.5rem;
      font-weight: bold;
      color: #1f2937;
    }
    .trend {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.875rem;
      font-weight: 600;
      margin-left: 0.5rem;
    }
    .trend.improving {
      background: #d1fae5;
      color: #065f46;
    }
    .trend.stable {
      background: #fef3c7;
      color: #92400e;
    }
    .trend.declining {
      background: #fee2e2;
      color: #991b1b;
    }
    .list {
      list-style: none;
      padding: 0;
    }
    .list li {
      padding: 0.5rem 0;
      border-bottom: 1px solid #e5e7eb;
    }
    .list li:last-child {
      border-bottom: none;
    }
    .scan-item {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1rem;
    }
    .scan-date {
      color: #6b7280;
      font-size: 0.875rem;
    }
    .scan-score {
      font-size: 1.25rem;
      font-weight: bold;
      color: #3b82f6;
      margin: 0.5rem 0;
    }
    .footer {
      text-align: center;
      margin-top: 3rem;
      padding-top: 2rem;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 0.875rem;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>GlowLens Monthly Report</h1>
    <p>${report.month} ${report.year} • Generated ${reportDate}</p>
    <p><strong>${userName}</strong></p>
  </div>

  <div class="section">
    <h2>Overview</h2>
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-label">Total Scans</div>
        <div class="stat-value">${report.totalScans}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Average GlowScore™</div>
        <div class="stat-value">
          ${report.averageGlowScore}
          <span class="trend ${report.glowScoreTrend}">
            ${report.glowScoreTrend === 'improving' ? '↑ Improving' : 
              report.glowScoreTrend === 'declining' ? '↓ Declining' : '→ Stable'}
          </span>
        </div>
      </div>
    </div>
  </div>

  ${report.scans.length > 0 ? `
  <div class="section">
    <h2>Scan History</h2>
    ${report.scans.map(scan => {
      const scanDate = format(new Date(scan.createdAt || scan.timestamp), 'MMM dd, yyyy');
      const score = scan.analysis?.skinHealthIndex || scan.glowScore || 0;
      return `
        <div class="scan-item">
          <div class="scan-date">${scanDate}</div>
          <div class="scan-score">GlowScore™: ${score}</div>
          ${scan.analysis?.acneSeverity ? `<div>Acne Severity: ${scan.analysis.acneSeverity}</div>` : ''}
        </div>
      `;
    }).join('')}
  </div>
  ` : ''}

  ${report.topRecommendations.length > 0 ? `
  <div class="section">
    <h2>Top Recommendations</h2>
    <ul class="list">
      ${report.topRecommendations.map(rec => `<li>${rec}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  ${report.progressHighlights.length > 0 ? `
  <div class="section">
    <h2>Progress Highlights</h2>
    <ul class="list">
      ${report.progressHighlights.map(highlight => `<li>${highlight}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  <div class="footer">
    <p>This report was generated by GlowLens AI Skincare Assistant</p>
    <p>For questions or support, contact VIP Support (24hr response)</p>
  </div>
</body>
</html>
  `.trim();
};

/**
 * Download report as PDF (using browser print to PDF)
 */
export const downloadReportAsPDF = (htmlContent: string, filename: string) => {
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};




